import { CommentItem } from './comment-item'
import { Skeleton } from '../ui/skeleton'
import React, { useState } from 'react'
import { useReplies } from 'src/hook/use-community'
import { CommunityComment } from 'src/types/community'

interface CommentThreadProps {
  comment: CommunityComment
  onReply: (commentId: string, username: string) => void
  small?: boolean
}

export const CommentThread = ({ comment, onReply, small }: CommentThreadProps): React.ReactElement => {
  const [showReplies, setShowReplies] = useState<boolean>(false)
  const repliesQuery = useReplies(comment.id, showReplies)

  const toggleReplies = (): void => {
    setShowReplies((prev) => !prev)
  }

  const replies = showReplies ? repliesQuery.data?.pages.flatMap((page) => page.data) ?? [] : comment.replies ?? []

  const hasMoreReplies = repliesQuery.hasNextPage ?? false

  const repliesCount = comment.repliesCount ?? replies.length ?? 0

  const handleReply = (): void => {
    const username = comment.hustler?.username ?? comment.hustler?.name ?? 'user'
    onReply(comment.id, username)
  }

  return (
    <div className="py-4">
      <CommentItem comment={comment} onReply={handleReply} small={small} />

      {repliesCount > 2 && !showReplies && (
        <div className="ml-[52px] mt-2">
          <button type="button" className="text-sm font-semibold text-primary hover:text-primary/80" onClick={toggleReplies}>
            View {repliesCount} {repliesCount === 1 ? 'reply' : 'replies'}
          </button>
        </div>
      )}

      {(showReplies || (replies.length > 0 && !showReplies && repliesCount <= 2)) && (
        <div className={`${small ? 'ml-[40px]' : 'ml-[52px]'} mt-3 border-l-2 border-white/10 pl-4 space-y-3`}>
          {replies.map((reply) => (
            <div key={reply.id} className="py-1">
              <CommentItem comment={reply} small={small} />
            </div>
          ))}
          {repliesQuery.isFetching && (
            <div className="space-y-2 py-1">
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          )}
          {hasMoreReplies && (
            <button
              type="button"
              className="text-sm font-semibold text-primary hover:text-primary/80"
              onClick={() => repliesQuery.fetchNextPage()}
              disabled={repliesQuery.isFetchingNextPage}
            >
              {repliesQuery.isFetchingNextPage ? 'Loading...' : 'Load more replies'}
            </button>
          )}
          {showReplies && (
            <button type="button" className="text-sm text-white/40 hover:text-white/60" onClick={toggleReplies}>
              Hide replies
            </button>
          )}
        </div>
      )}
    </div>
  )
}
